// =============================================================================
// File: frontend/src/providers/ThemeContext.tsx
// =============================================================================
// Provider minimo che imposta data-theme="light|dark" su <html>
// e persiste la scelta in localStorage.
// =============================================================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ThemeMode = "light" | "dark";

// Dark mode disabled: revert 2026-04-30 dopo regressione colori in light.
// Tema forzato light, toggle nascosto, localStorage pulito.
export const isDarkModeEnabled = false;

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "light",
  setTheme: () => {},
  toggleTheme: () => {},
});

const STORAGE_KEY = "leopart.theme";

function readInitialTheme(): ThemeMode {
  if (!isDarkModeEnabled) return "light";
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;
  // Default: rispetta preferenza OS, ma fallback a light
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<ThemeMode>(readInitialTheme);

  // Applica al <html> ogni volta che cambia
  useEffect(() => {
    const applied: ThemeMode = isDarkModeEnabled ? theme : "light";
    document.documentElement.setAttribute("data-theme", applied);
    if (isDarkModeEnabled) {
      window.localStorage.setItem(STORAGE_KEY, applied);
    } else {
      // In prod: rimuovi qualsiasi residuo di scelta utente.
      window.localStorage.removeItem(STORAGE_KEY);
    }
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme: isDarkModeEnabled ? theme : "light",
      setTheme: isDarkModeEnabled ? setThemeState : () => {},
      toggleTheme: isDarkModeEnabled
        ? () => setThemeState((t) => (t === "dark" ? "light" : "dark"))
        : () => {},
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

// =============================================================================
// Integrazione in src/index.tsx
// =============================================================================
//
// import { ThemeProvider } from "./providers/ThemeContext";
//
// root.render(
//   <QueryClientProvider client={queryClient}>
//     <Provider store={store}>
//       <ChakraProvider>
//         <ThemeProvider>                  {/* <-- AGGIUNGI QUI */}
//           <AuthProvider>
//             <ConfirmModalProvider>
//               ...
//             </ConfirmModalProvider>
//           </AuthProvider>
//         </ThemeProvider>                 {/* <-- /chiudi */}
//       </ChakraProvider>
//     </Provider>
//   </QueryClientProvider>
// );
